import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Label } from "@/components/ui/label";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { clientApi, Client } from "@/services/api";
import { ScrollArea } from "@/components/ui/scroll-area";
import { workoutPlanApi, clientWorkoutApi, WorkoutPlan, exerciseApi, Exercise } from "@/services/api";

// Mock workout data (for display only)
const mockWorkoutExercises = [
  { id: 1, name: "Bench Press", category: "Chest", sets: 4, reps: "8-10", weight: "varies", difficulty: "Intermediate" },
  { id: 2, name: "Incline Dumbbell Press", category: "Chest", sets: 4, reps: "8-12", weight: "varies", difficulty: "Intermediate" },
  { id: 3, name: "Chest Fly", category: "Chest", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 4, name: "Push-ups", category: "Chest", sets: 3, reps: "15-20", weight: "bodyweight", difficulty: "Beginner" },
  { id: 5, name: "Cable Chest Crossover", category: "Chest", sets: 3, reps: "12-15", weight: "varies", difficulty: "Intermediate" },
  { id: 6, name: "Dumbbell Pullover", category: "Chest", sets: 3, reps: "12", weight: "varies", difficulty: "Intermediate" },
  { id: 7, name: "Machine Chest Press", category: "Chest", sets: 3, reps: "10-12", weight: "varies", difficulty: "Beginner" },

  { id: 8, name: "Squat", category: "Legs", sets: 4, reps: "8-12", weight: "varies", difficulty: "Intermediate" },
  { id: 9, name: "Leg Press", category: "Legs", sets: 4, reps: "10-12", weight: "varies", difficulty: "Intermediate" },
  { id: 10, name: "Lunges", category: "Legs", sets: 3, reps: "12 each leg", weight: "varies", difficulty: "Beginner" },
  { id: 11, name: "Romanian Deadlift", category: "Legs", sets: 3, reps: "8-10", weight: "varies", difficulty: "Intermediate" },
  { id: 12, name: "Leg Extensions", category: "Legs", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 13, name: "Calf Raises", category: "Legs", sets: 3, reps: "15-20", weight: "varies", difficulty: "Beginner" },
  { id: 14, name: "Bulgarian Split Squat", category: "Legs", sets: 3, reps: "10 each leg", weight: "varies", difficulty: "Intermediate" },
  { id: 15, name: "Goblet Squat", category: "Legs", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 16, name: "Hip Thrust", category: "Legs", sets: 4, reps: "10-12", weight: "varies", difficulty: "Intermediate" },
  { id: 17, name: "Sumo Deadlift", category: "Legs", sets: 4, reps: "6-8", weight: "varies", difficulty: "Advanced" },
  { id: 18, name: "Step-Ups", category: "Legs", sets: 3, reps: "12 each leg", weight: "varies", difficulty: "Beginner" },
  { id: 19, name: "Glute Kickbacks", category: "Legs", sets: 3, reps: "15", weight: "varies", difficulty: "Beginner" },

  { id: 20, name: "Deadlift", category: "Back", sets: 3, reps: "6-8", weight: "varies", difficulty: "Advanced" },
  { id: 21, name: "Pull-ups", category: "Back", sets: 3, reps: "8-12", weight: "bodyweight", difficulty: "Intermediate" },
  { id: 22, name: "Lat Pulldown", category: "Back", sets: 3, reps: "10-12", weight: "varies", difficulty: "Beginner" },
  { id: 23, name: "Seated Cable Row", category: "Back", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 24, name: "Barbell Row", category: "Back", sets: 4, reps: "8-10", weight: "varies", difficulty: "Intermediate" },
  { id: 25, name: "Face Pull", category: "Back", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 26, name: "Chin-ups", category: "Back", sets: 3, reps: "8-12", weight: "bodyweight", difficulty: "Intermediate" },
  { id: 27, name: "Reverse Fly", category: "Back", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 28, name: "Good Mornings", category: "Back", sets: 3, reps: "12", weight: "varies", difficulty: "Intermediate" },
  { id: 29, name: "T-Bar Row", category: "Back", sets: 4, reps: "8-10", weight: "varies", difficulty: "Intermediate" },
  { id: 30, name: "Single Arm Dumbbell Row", category: "Back", sets: 3, reps: "12", weight: "varies", difficulty: "Intermediate" },

  { id: 31, name: "Shoulder Press", category: "Shoulders", sets: 3, reps: "10-12", weight: "varies", difficulty: "Intermediate" },
  { id: 32, name: "Lateral Raises", category: "Shoulders", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 33, name: "Front Raises", category: "Shoulders", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 34, name: "Arnold Press", category: "Shoulders", sets: 3, reps: "10-12", weight: "varies", difficulty: "Intermediate" },
  { id: 35, name: "Shrugs", category: "Shoulders", sets: 4, reps: "15-20", weight: "varies", difficulty: "Beginner" },

  { id: 36, name: "Bicep Curls", category: "Arms", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 37, name: "Hammer Curls", category: "Arms", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 38, name: "Barbell Curl", category: "Arms", sets: 3, reps: "10-12", weight: "varies", difficulty: "Intermediate" },
  { id: 39, name: "Tricep Extensions", category: "Arms", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },
  { id: 40, name: "Tricep Dips", category: "Arms", sets: 3, reps: "8-12", weight: "bodyweight", difficulty: "Intermediate" },
  { id: 41, name: "Cable Pushdowns", category: "Arms", sets: 3, reps: "12-15", weight: "varies", difficulty: "Beginner" },

  { id: 42, name: "Plank", category: "Core", sets: 3, reps: "30-60 sec", weight: "bodyweight", difficulty: "Beginner" },
  { id: 43, name: "Russian Twists", category: "Core", sets: 3, reps: "20", weight: "bodyweight", difficulty: "Beginner" },
  { id: 44, name: "Leg Raises", category: "Core", sets: 3, reps: "15-20", weight: "bodyweight", difficulty: "Beginner" },
  { id: 45, name: "Cable Crunch", category: "Core", sets: 3, reps: "15-20", weight: "varies", difficulty: "Beginner" },
  { id: 46, name: "Mountain Climbers", category: "Core", sets: 3, reps: "30-45 sec", weight: "bodyweight", difficulty: "Beginner" },

  { id: 47, name: "Burpees", category: "Full Body", sets: 3, reps: "15-20", weight: "bodyweight", difficulty: "Intermediate" },
  { id: 48, name: "Kettlebell Swings", category: "Full Body", sets: 3, reps: "20", weight: "varies", difficulty: "Intermediate" },
  { id: 49, name: "Farmer's Walk", category: "Full Body", sets: 3, reps: "30 sec", weight: "varies", difficulty: "Intermediate" },
  { id: 50, name: "Medicine Ball Slams", category: "Full Body", sets: 3, reps: "15-20", weight: "varies", difficulty: "Beginner" },
  { id: 51, name: "Box Jumps", category: "Full Body", sets: 3, reps: "10-15", weight: "bodyweight", difficulty: "Intermediate" },
  { id: 52, name: "Battle Ropes", category: "Full Body", sets: 3, reps: "30 sec", weight: "bodyweight", difficulty: "Intermediate" },
  { id: 53, name: "Clean and Press", category: "Full Body", sets: 4, reps: "6-8", weight: "varies", difficulty: "Advanced" },
  { id: 54, name: "Turkish Get-up", category: "Full Body", sets: 3, reps: "5 each side", weight: "varies", difficulty: "Advanced" }
];

interface WorkoutDay {
  id: string;
  name: string;
  exercises: WorkoutExercise[];
}

interface WorkoutExercise {
  exercise: Exercise;
  sets: number;
  reps: string;
  weight: string;
  notes?: string;
}

const Workouts = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [showNewProgramDialog, setShowNewProgramDialog] = useState(false);
  const [showAddExerciseDrawer, setShowAddExerciseDrawer] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [programName, setProgramName] = useState("");
  const [selectedGoal, setSelectedGoal] = useState("");
  const [workoutDays, setWorkoutDays] = useState<WorkoutDay[]>([]);
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [exerciseSets, setExerciseSets] = useState(3);
  const [exerciseReps, setExerciseReps] = useState("8-12");
  const [exerciseWeight, setExerciseWeight] = useState("varies");
  const [exerciseNotes, setExerciseNotes] = useState("");
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentPlans, setRecentPlans] = useState<WorkoutPlan[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [exercisesError, setExercisesError] = useState<string | null>(null);
  const [newExerciseName, setNewExerciseName] = useState("");
  const [newExerciseCategory, setNewExerciseCategory] = useState("");
  const [newExerciseTargetMuscle, setNewExerciseTargetMuscle] = useState("");
  const [newExerciseDifficulty, setNewExerciseDifficulty] = useState("");
  const [newExerciseInstructions, setNewExerciseInstructions] = useState("");
  const [newExerciseError, setNewExerciseError] = useState<string | null>(null);
  const [savingExercise, setSavingExercise] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [deletingExerciseId, setDeletingExerciseId] = useState<string | null>(null);
  
  // Plan editing state
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  
  const filteredExercises = exercises.filter((exercise) => {
    const matchesSearch = exercise.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter ? exercise.category === categoryFilter : true;
    return matchesSearch && matchesCategory;
  });
  
  const categories = Array.from(new Set(exercises.map((exercise) => exercise.category)));

  // Fetch recent plans for selected client
  const fetchRecentPlans = async (clientId: string) => {
    if (!clientId) return;
    try {
      const plans = await workoutPlanApi.getAll(clientId);
      setRecentPlans(plans);
    } catch (err) {
      setRecentPlans([]);
    }
  };

  // Fetch plans when client changes
  useEffect(() => {
    if (selectedClient) {
      fetchRecentPlans(selectedClient);
      // Reset plan selection when client changes
      setSelectedPlanId(null);
      setEditingPlanId(null);
    } else {
      setRecentPlans([]);
      setSelectedPlanId(null);
      setEditingPlanId(null);
    }
  }, [selectedClient]);

  // Load plan for editing when selectedPlanId changes
  useEffect(() => {
    if (!selectedPlanId || selectedPlanId === 'new') {
      // Clear form for new plan
      setEditingPlanId(null);
      setProgramName("");
      setSelectedGoal("");
      setWorkoutDays([]);
      setSelectedDayId(null);
      setSelectedExercise(null);
      return;
    }

    const plan = recentPlans.find(p => p._id === selectedPlanId);
    if (plan) {
      setEditingPlanId(plan._id);
      setProgramName(plan.name);
      setSelectedGoal(plan.goals || "");
      
      // Convert workoutSessions to workoutDays format
      const convertedDays: WorkoutDay[] = plan.workoutSessions.map((session, idx) => ({
        id: `day-${idx}-${Date.now()}`,
        name: session.name,
        exercises: session.exercises.map(ex => {
          // Find the exercise object from our exercises list
          const exercise = exercises.find(e => e._id === ex.exercise);
          if (exercise) {
            return {
              exercise,
              sets: ex.sets || 3,
              reps: ex.reps?.toString() || "8-12",
              weight: ex.weight?.toString() || "varies",
              notes: ex.notes || ""
            };
          }
          // Fallback if exercise not found
          return {
            exercise: {
              _id: ex.exercise as string,
              name: "Unknown Exercise",
              category: "Unknown",
              targetMuscleGroup: "Unknown",
              difficulty: "beginner",
              instructions: "",
              equipment: []
            } as Exercise,
            sets: ex.sets || 3,
            reps: ex.reps?.toString() || "8-12",
            weight: ex.weight?.toString() || "varies",
            notes: ex.notes || ""
          };
        })
      }));
      
      setWorkoutDays(convertedDays);
      if (convertedDays.length > 0) {
        setSelectedDayId(convertedDays[0].id);
      }
    }
  }, [selectedPlanId, recentPlans, exercises]);

  // Fetch clients from backend
  useEffect(() => {
    setClientsLoading(true);
    setClientsError(null);
    clientApi.getAll()
      .then(setClients)
      .catch((err) => {
        setClientsError("Failed to load clients");
        setClients([]);
      })
      .finally(() => setClientsLoading(false));
  }, []);

  // Fetch exercises from backend
  useEffect(() => {
    setExercisesLoading(true);
    setExercisesError(null);
    exerciseApi.getAll()
      .then(setExercises)
      .catch(() => {
        setExercisesError("Failed to load exercises");
        setExercises([]);
      })
      .finally(() => setExercisesLoading(false));
  }, []);

  // Reset form on open/close
  useEffect(() => {
    if (!showAddExerciseDrawer) {
      setNewExerciseName("");
      setNewExerciseCategory("");
      setNewExerciseTargetMuscle("");
      setNewExerciseDifficulty("");
      setNewExerciseInstructions("");
      setNewExerciseError(null);
      setShowNewCategoryInput(false);
    }
  }, [showAddExerciseDrawer]);

  const handleCreateNewProgram = () => {
    setShowNewProgramDialog(true);
  };

  const handleAddNewExercise = () => {
    setShowAddExerciseDrawer(true);
  };

  const handleSaveProgram = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!selectedClient || !programName || workoutDays.length === 0) {
        setError("Please fill all required fields and add at least one workout day.");
        setSaving(false);
        return;
      }
      
      // Transform workoutDays to workoutSessions
      const workoutSessions = workoutDays.map((day) => ({
        name: day.name,
        exercises: day.exercises.map((ex) => {
          // ex.exercise is an Exercise object from the backend
          const exerciseId = ex.exercise._id;
          return {
            exercise: exerciseId,
            sets: ex.sets,
            reps: typeof ex.reps === "string" ? parseInt(ex.reps) || 1 : ex.reps,
            weight: typeof ex.weight === "string" ? parseFloat(ex.weight) || 0 : ex.weight,
            notes: ex.notes,
          };
        }),
      }));
      
      if (editingPlanId) {
        // Update existing plan
        const updatedPlan = {
          name: programName,
          workoutSessions,
          goals: selectedGoal,
          status: 'active' as const,
        };
        await workoutPlanApi.update(editingPlanId, updatedPlan);
        toast({
          title: "Workout Program Updated",
          description: "Workout program has been updated successfully.",
          variant: "default",
        });
      } else {
        // Create new plan
        const plan: Omit<WorkoutPlan, '_id'> = {
          client: selectedClient,
          name: programName,
          workoutSessions,
          startDate: new Date().toISOString(),
          goals: selectedGoal,
          status: 'active',
        };
        const createdPlan = await workoutPlanApi.create(plan);
        // Associate with client
        await clientWorkoutApi.addWorkout(selectedClient, createdPlan._id!);
        toast({
          title: "Workout Program Created",
          description: "New workout program has been created and assigned to the client.",
          variant: "default",
        });
      }
      
      setShowNewProgramDialog(false);
      setSelectedDayId(null);
      setSelectedExercise(null);
      setExerciseSets(3);
      setExerciseReps("8-12");
      setExerciseWeight("varies");
      setExerciseNotes("");
      setProgramName("");
      setSelectedGoal("");
      setWorkoutDays([]);
      setSelectedPlanId(null);
      setEditingPlanId(null);
      fetchRecentPlans(selectedClient);
    } catch (err: any) {
      setError(err.message || "Failed to save workout program.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExercise = async () => {
    setNewExerciseError(null);
    if (!newExerciseName || !newExerciseCategory || !newExerciseTargetMuscle || !newExerciseDifficulty || !newExerciseInstructions) {
      setNewExerciseError("Please fill all required fields.");
      return;
    }
    setSavingExercise(true);
    try {
      const payload = {
        name: newExerciseName,
        category: newExerciseCategory,
        targetMuscleGroup: newExerciseTargetMuscle,
        difficulty: newExerciseDifficulty as 'beginner' | 'intermediate' | 'advanced',
        instructions: newExerciseInstructions,
        equipment: [],
      };
      const created = await exerciseApi.create(payload);
      setExercises(prev => [...prev, created]);
      setShowAddExerciseDrawer(false);
      toast({
        title: "Exercise Added",
        description: "New exercise has been added to the database.",
        variant: "default",
      });
    } catch (err: any) {
      setNewExerciseError(err.message || "Failed to add exercise.");
    } finally {
      setSavingExercise(false);
    }
  };

  const handleAddDay = () => {
    const newDay: WorkoutDay = {
      id: `day-${Date.now()}`,
      name: `Day ${workoutDays.length + 1}`,
      exercises: []
    };
    setWorkoutDays([...workoutDays, newDay]);
    setSelectedDayId(newDay.id);
  };

  const handleRemoveDay = (dayId: string) => {
    setWorkoutDays(workoutDays.filter(day => day.id !== dayId));
    if (selectedDayId === dayId) {
      setSelectedDayId(null);
    }
  };

  const handleAddExerciseToDay = (dayId: string, exercise: Exercise) => {
    if (!dayId || !exercise) return;

    const day = workoutDays.find(d => d.id === dayId);
    if (!day) return;

    // Check if exercise already exists in the day
    const exerciseExists = day.exercises.some(ex => ex.exercise._id === exercise._id);
    if (exerciseExists) {
      toast({
        title: "Exercise already added",
        description: "This exercise is already in the workout day. You can adjust its details instead.",
        variant: "destructive"
      });
      return;
    }

    setWorkoutDays(workoutDays.map(d => 
      d.id === dayId 
        ? { 
            ...d, 
            exercises: [...d.exercises, { 
              exercise, 
              sets: exerciseSets,
              reps: exerciseReps,
              weight: exerciseWeight,
              notes: exerciseNotes
            }] 
          }
        : d
    ));
  };

  const handleRemoveExerciseFromDay = (dayId: string, exerciseId: string) => {
    setWorkoutDays(workoutDays.map(day => 
      day.id === dayId 
        ? { ...day, exercises: day.exercises.filter(ex => ex.exercise._id !== exerciseId) }
        : day
    ));
  };

  const handleUpdateExerciseDetails = (dayId: string, exerciseId: string, field: string, value: string | number) => {
    setWorkoutDays(workoutDays.map(day => {
      if (day.id === dayId) {
        return {
          ...day,
          exercises: day.exercises.map(ex => {
            if (ex.exercise._id === exerciseId) {
              return { ...ex, [field]: value };
            }
            return ex;
          })
        };
      }
      return day;
    }));
  };

  const handleCloseModal = () => {
    setShowNewProgramDialog(false);
    setSelectedDayId(null);
    setSelectedExercise(null);
    setExerciseSets(3);
    setExerciseReps("8-12");
    setExerciseWeight("varies");
    setExerciseNotes("");
    setProgramName("");
    setSelectedGoal("");
    setWorkoutDays([]);
    setSelectedPlanId(null);
    setEditingPlanId(null);
    setShowCloseConfirmation(false);
  };

  const handleCloseAttempt = () => {
    if (programName || workoutDays.length > 0) {
      setShowCloseConfirmation(true);
    } else {
      handleCloseModal();
    }
  };

  const handleDeleteExercise = async (exerciseId: string) => {
    if (!window.confirm('Are you sure you want to delete this exercise?')) return;
    setDeletingExerciseId(exerciseId);
    try {
      await exerciseApi.delete(exerciseId);
      setExercises(prev => prev.filter(e => e._id !== exerciseId));
      toast({
        title: 'Exercise Deleted',
        description: 'The exercise has been removed from the database.',
        variant: 'default',
      });
    } catch (err: any) {
      toast({
        title: 'Delete Failed',
        description: err.message || 'Failed to delete exercise.',
        variant: 'destructive',
      });
    } finally {
      setDeletingExerciseId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Workout Plans</h1>
          <p className="text-muted-foreground">
            Design and track workout programs for your clients
          </p>
        </div>
        <Button onClick={handleCreateNewProgram}>
          <Plus className="mr-2 h-4 w-4" /> Create New Program
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Exercise Database</CardTitle>
          <CardDescription>
            Browse all exercises with detailed instructions
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search exercises..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Exercise</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Sets</TableHead>
                  <TableHead className="text-right">Reps</TableHead>
                  <TableHead className="text-right">Weight</TableHead>
                  <TableHead>Difficulty</TableHead>
                  <TableHead>Delete</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExercises.map((exercise) => (
                  <TableRow key={exercise._id}>
                    <TableCell className="font-medium">{exercise.name}</TableCell>
                    <TableCell>{exercise.category}</TableCell>
                    <TableCell className="text-right">-</TableCell>
                    <TableCell className="text-right">-</TableCell>
                    <TableCell className="text-right">-</TableCell>
                    <TableCell>{exercise.difficulty}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteExercise(exercise._id)}
                        disabled={deletingExerciseId === exercise._id}
                        aria-label="Delete Exercise"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredExercises.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                      No exercises found matching your search criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>

        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={handleAddNewExercise}>
            <Plus className="mr-2 h-4 w-4" /> Add New Exercise
          </Button>
          <Button onClick={handleCreateNewProgram}>Create Workout Plan</Button>
        </CardFooter>
      </Card>

      <Dialog open={showNewProgramDialog} onOpenChange={handleCloseAttempt}>
        <DialogContent className="max-w-[1400px] w-[95vw] h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editingPlanId ? 'Edit Workout Program' : 'Create New Workout Program'}</DialogTitle>
            <DialogDescription>
              {editingPlanId ? 'Modify the existing workout program' : 'Design a comprehensive workout program for your client'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-12 gap-4 p-1">
            {/* Left Column - Client Selection and Program Details */}
            <div className="col-span-3 space-y-3">
              <div className="space-y-1.5">
                <Label>Select Client</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientsLoading && <div className="p-2 text-muted-foreground">Loading clients...</div>}
                    {clientsError && <div className="p-2 text-red-500">{clientsError}</div>}
                    {clients.map((client) => (
                      <SelectItem key={client._id} value={client._id!}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Select Plan</Label>
                <Select value={selectedPlanId || 'new'} onValueChange={setSelectedPlanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Create new or select existing plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">+ Create New Plan</SelectItem>
                    {recentPlans.map((plan) => (
                      <SelectItem key={plan._id} value={plan._id!}>
                        {plan.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Program Name</Label>
                <Input
                  placeholder="Enter program name"
                  value={programName}
                  onChange={(e) => setProgramName(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Goal</Label>
                <Select value={selectedGoal} onValueChange={setSelectedGoal}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select goal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strength">Strength</SelectItem>
                    <SelectItem value="hypertrophy">Hypertrophy</SelectItem>
                    <SelectItem value="endurance">Endurance</SelectItem>
                    <SelectItem value="weight-loss">Weight Loss</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <Label>Workout Days</Label>
                  <Button onClick={handleAddDay} size="sm">
                    <Plus className="mr-2 h-4 w-4" /> Add Day
                  </Button>
                </div>
                <ScrollArea className="h-[250px] border rounded-lg">
                  <div className="p-2 space-y-1.5">
                    {workoutDays.length === 0 ? (
                      <div className="text-center text-muted-foreground py-4">
                        No workout days added yet. Click "Add Day" to start.
                      </div>
                    ) : (
                      workoutDays.map((day) => (
                        <div
                          key={day.id}
                          className={`p-2 rounded-md cursor-pointer transition-colors ${
                            selectedDayId === day.id
                              ? "bg-primary text-primary-foreground"
                              : "hover:bg-muted"
                          }`}
                          onClick={() => setSelectedDayId(day.id)}
                        >
                          <div className="flex items-center justify-between">
                            <Input
                              value={day.name}
                              onChange={(e) => {
                                setWorkoutDays(workoutDays.map(d => 
                                  d.id === day.id ? { ...d, name: e.target.value } : d
                                ));
                              }}
                              className="w-[100px] bg-transparent border-none focus-visible:ring-0"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveDay(day.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                          <div className="text-sm opacity-80">
                            {day.exercises.length} exercises
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>

            {/* Middle Column - Exercise Selection */}
            <div className="col-span-3 space-y-3">
              <div className="space-y-1.5">
                <Label>Search Exercises</Label>
                <Input
                  placeholder="Search by name or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <ScrollArea className="h-[450px] border rounded-lg">
                <div className="p-2 space-y-1.5">
                  {filteredExercises.map((exercise) => (
                    <div
                      key={exercise._id}
                      className={`p-2 rounded-md cursor-pointer transition-colors ${
                        selectedExercise?._id === exercise._id
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      }`}
                      onClick={() => {
                        setSelectedExercise(exercise);
                        setExerciseSets(3);
                        setExerciseReps("8-12");
                        setExerciseWeight("varies");
                      }}
                    >
                      <div className="font-medium">{exercise.name}</div>
                      <div className="text-sm opacity-80">
                        {exercise.category} • {exercise.difficulty}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Right Column - Exercise Details and Selected Exercises */}
            <div className="col-span-6 space-y-3">
              {selectedExercise ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">{selectedExercise.name}</h3>
                      <div className="text-sm text-muted-foreground">
                        {selectedExercise.category} • {selectedExercise.difficulty}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => selectedDayId && handleAddExerciseToDay(selectedDayId, selectedExercise)}
                      disabled={!selectedDayId}
                    >
                      {!selectedDayId ? "Select a Day First" : "Add to Day"}
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Sets</Label>
                      <Input
                        type="number"
                        min="1"
                        value={exerciseSets}
                        onChange={(e) => setExerciseSets(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-full text-center"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label>Reps</Label>
                      <Input
                        value={exerciseReps}
                        onChange={(e) => setExerciseReps(e.target.value)}
                        className="w-full text-center"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Weight</Label>
                    <Input
                      value={exerciseWeight}
                      onChange={(e) => setExerciseWeight(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label>Notes</Label>
                    <Input
                      value={exerciseNotes}
                      onChange={(e) => setExerciseNotes(e.target.value)}
                      placeholder="Add any specific notes or instructions..."
                      className="w-full"
                    />
                  </div>

                  {selectedDayId && (
                    <div className="space-y-1.5">
                      <h4 className="font-medium">Selected Exercises</h4>
                      <ScrollArea className="h-[200px] border rounded-lg">
                        <div className="p-2 space-y-1.5">
                          {workoutDays.find(d => d.id === selectedDayId)?.exercises.map((workoutExercise, idx) => (
                            <div
                              key={(workoutExercise.exercise as Exercise)._id}
                              className="p-2 hover:bg-muted rounded-md"
                            >
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="font-medium">{(workoutExercise.exercise as Exercise).name}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveExerciseFromDay(selectedDayId, (workoutExercise.exercise as Exercise)._id)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-sm text-muted-foreground">
                                <div>
                                  <Label className="text-xs">Sets</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={workoutExercise.sets}
                                    onChange={(e) => handleUpdateExerciseDetails(
                                      selectedDayId,
                                      (workoutExercise.exercise as Exercise)._id,
                                      'sets',
                                      Math.max(1, parseInt(e.target.value) || 1)
                                    )}
                                    className="w-full text-center"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Reps</Label>
                                  <Input
                                    value={workoutExercise.reps}
                                    onChange={(e) => handleUpdateExerciseDetails(
                                      selectedDayId,
                                      (workoutExercise.exercise as Exercise)._id,
                                      'reps',
                                      e.target.value
                                    )}
                                    className="w-full text-center"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Weight</Label>
                                  <Input
                                    value={workoutExercise.weight}
                                    onChange={(e) => handleUpdateExerciseDetails(
                                      selectedDayId,
                                      (workoutExercise.exercise as Exercise)._id,
                                      'weight',
                                      e.target.value
                                    )}
                                    className="w-full text-center"
                                  />
                                </div>
                              </div>
                              {workoutExercise.notes && (
                                <div className="mt-1.5 text-sm text-muted-foreground">
                                  Notes: {workoutExercise.notes}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  Select an exercise to view details
                </div>
              )}
            </div>
          </div>
          </div>

          <DialogFooter className="flex-shrink-0">
            {error && <div className="text-red-500 text-sm mr-4">{error}</div>}
            <Button variant="outline" onClick={handleCloseAttempt} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveProgram} disabled={saving}>
              {saving ? "Saving..." : (editingPlanId ? "Update Program" : "Create Program")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Close</DialogTitle>
            <DialogDescription>
              Are you sure you want to close? All unsaved changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseConfirmation(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleCloseModal}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Drawer open={showAddExerciseDrawer} onOpenChange={setShowAddExerciseDrawer}>
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>Add New Exercise</DrawerTitle>
            <DrawerDescription>
              Add a new exercise to the database
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 py-2">
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input
                  id="name"
                  className="col-span-3"
                  placeholder="Exercise name"
                  value={newExerciseName}
                  onChange={e => setNewExerciseName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="category" className="text-right">
                  Category
                </Label>
                {showNewCategoryInput ? (
                  <Input
                    className="col-span-3"
                    placeholder="New category"
                    value={newExerciseCategory}
                    onChange={e => setNewExerciseCategory(e.target.value)}
                    onBlur={() => setShowNewCategoryInput(false)}
                    autoFocus
                  />
                ) : (
                  <Select value={newExerciseCategory} onValueChange={val => {
                    if (val === "new") {
                      setShowNewCategoryInput(true);
                      setNewExerciseCategory("");
                    } else {
                      setNewExerciseCategory(val);
                    }
                  }}>
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                      <SelectItem value="new">+ Add New Category</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="targetMuscle" className="text-right">
                  Target Muscle Group
                </Label>
                <Input
                  id="targetMuscle"
                  className="col-span-3"
                  placeholder="e.g. Chest, Back, Legs"
                  value={newExerciseTargetMuscle}
                  onChange={e => setNewExerciseTargetMuscle(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="difficulty" className="text-right">
                  Difficulty
                </Label>
                <Select value={newExerciseDifficulty} onValueChange={setNewExerciseDifficulty}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="instructions" className="text-right">
                  Instructions
                </Label>
                <Input
                  id="instructions"
                  className="col-span-3"
                  placeholder="e.g. 3 sets of 10 reps, use moderate weight"
                  value={newExerciseInstructions}
                  onChange={e => setNewExerciseInstructions(e.target.value)}
                />
              </div>
              {newExerciseError && (
                <div className="col-span-4 text-red-500 text-sm">{newExerciseError}</div>
              )}
            </div>
          </div>
          <DrawerFooter className="pt-2">
            <DrawerClose asChild>
              <Button variant="outline" disabled={savingExercise}>Cancel</Button>
            </DrawerClose>
            <Button onClick={handleSaveExercise} disabled={savingExercise}>
              {savingExercise ? "Saving..." : "Save Exercise"}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default Workouts;
